import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { GpAsteriskService, InternalCallModel, InternalCallUpdate, TransferTargetModel } from 'gp-asterisk';
import { firstValueFrom, Subject } from 'rxjs';
import { ProfileState } from 'shared';
import { SoftphoneService } from './softphone.service';

const FAIL_MESSAGES: Partial<Record<NonNullable<InternalCallModel['reason']>, string>> = {
  'no-answer': 'не відповідає',
  unavailable: 'недоступний — немає ні SIP-телефону, ні застосунку на зв’язку'
};

/**
 * Внутрішні дзвінки між співробітниками. АТС спершу з'єднується з вами (лінія вбудованого телефону або
 * ваш SIP-телефон), ви чуєте гудки, а колезі дзвонить SIP-телефон і приходить дзвінок у застосунок.
 * Стан дзвінка бекенд надсилає подією WebSocket `[Asterisk] InternalCall`.
 */
@Injectable({ providedIn: 'root' })
export class InternalCallService {
  // останній відомий стан внутрішніх дзвінків за id (для вхідних: ім'я того, хто дзвонить)
  private readonly calls = new Map<string, InternalCallModel>();
  readonly updates$ = new Subject<InternalCallModel>();

  constructor(
    actions$: Actions,
    private store: Store,
    private asteriskService: GpAsteriskService,
    private softphone: SoftphoneService,
    private toastController: ToastController
  ) {
    actions$.pipe(ofActionDispatched(InternalCallUpdate)).subscribe(({ payload }: InternalCallUpdate) => this.onUpdate(payload));
  }

  // внутрішній дзвінок, у якому ми зараз розмовляємо через застосунок (вихідний — з відповіді бекенду, вхідний — при відповіді)
  private activeId: string | null = null;

  get(id: string): InternalCallModel | undefined {
    return this.calls.get(id);
  }

  setActive(id: string | null): void {
    this.activeId = id;
  }

  // Дзвінок колезі. true — з'єднання почалося (через застосунок або на ваш SIP-телефон)
  async call(target: TransferTargetModel): Promise<boolean> {
    const label = `${target.name} · ${target.username}`;
    const request = () => firstValueFrom(this.asteriskService.startInternalCall(target.username))
      .then(model => {
        this.activeId = model?.id ?? null;
        return model;
      });
    // без внутрішнього номера (напр. майстер) дзвонити можна лише з телефону в застосунку — він і створює номер
    if (!this.softphone.isActive && !this.store.selectSnapshot(ProfileState.currentUser)?.code) {
      this.toast('Увімкніть «Приймати дзвінки в застосунку» — так з’явиться ваш внутрішній номер', 'warning');
      return false;
    }
    try {
      if (this.softphone.isActive) {
        // лінія застосунку: статус «Виклик…», доки колега не відповість
        await this.softphone.withLine(label, request, true, { direction: 'outgoing', number: target.username, name: target.name });
      } else {
        await request();
        this.toast('Зараз задзвонить ваш телефон — підніміть трубку, і піде виклик', 'success');
      }
      return true;
    } catch (err: any) {
      this.toast(err?.error?.message || err?.message || 'Не вдалося зателефонувати', 'danger');
      return false;
    }
  }

  private onUpdate(model: InternalCallModel): void {
    if (model.status === 'ended') this.calls.delete(model.id);
    else this.calls.set(model.id, model);
    this.updates$.next(model);

    // співрозмовник поклав трубку: якщо сигнал завершення від АТС до застосунку загубився — закриваємо розмову самі
    if (model.status === 'ended' && model.id === this.activeId) {
      this.activeId = null;
      if (this.softphone.state$.value.call) this.softphone.endCall();
    }

    // зустрічний дзвінок: ми набрали колегу, а бекенд з'єднав нас із його дзвінком нам — це той самий дзвінок (activeId)
    if (model.status === 'active' && model.id === this.activeId) this.softphone.markAnswered();

    const mySip = this.store.selectSnapshot(ProfileState.currentUser)?.code;
    if (!mySip || model.fromSip !== mySip) return;
    if (model.status === 'active') this.softphone.markAnswered();
    // доставка дзвінка в застосунок колеги
    if (model.status === 'ringing' && model.delivery === 'delivered') this.softphone.setCallNote('Дзвонить у колеги на телефоні');
    if (model.status === 'ringing' && model.delivery === 'unreachable') this.softphone.setCallNote('Застосунок колеги поза мережею');
    const fail = model.status === 'ended' && model.reason ? FAIL_MESSAGES[model.reason] : undefined;
    if (fail) this.toast(`${model.toName || model.toSip} ${fail}`, 'warning');
  }

  private async toast(message: string, color: 'success' | 'warning' | 'danger') {
    const toast = await this.toastController.create({ message, color, duration: 3500, position: 'top' });
    await toast.present();
  }
}
